import Keycloak from 'keycloak-js';
import { runtimeConfig, validateConfig } from '../../config/runtimeConfig';

const configValidation = validateConfig();
if (!configValidation.valid) {
  console.warn('⚠️  Missing Keycloak configuration values:', configValidation.missing);
}

export const keycloakConfig = {
  url: runtimeConfig.keycloakUrl || '',
  realm: runtimeConfig.keycloakRealm || '',
  clientId: runtimeConfig.keycloakClientId || '',
};

export const isKeycloakConfigValid = !!(
  keycloakConfig.url &&
  keycloakConfig.realm &&
  keycloakConfig.clientId
);

let keycloakInstance: any = null;

try {
  if (typeof Keycloak === 'function') {
    keycloakInstance = new (Keycloak as any)(keycloakConfig);
  } else if (Keycloak && typeof (Keycloak as any).default === 'function') {
    keycloakInstance = new (Keycloak as any).default(keycloakConfig);
  } else if (typeof Keycloak === 'object' && Keycloak !== null) {
    keycloakInstance = (Keycloak as any)(keycloakConfig);
  } else {
    throw new Error(`Keycloak import is invalid. Type: ${typeof Keycloak}, Value: ${Keycloak}`);
  }
  if (!keycloakInstance) {
    throw new Error('Keycloak instance is null after initialization attempt');
  }
} catch {
  // Handled by AuthContext
}

export const keycloak = keycloakInstance;

/**
 * No onLoad mode: app shows LoginPage until the user signs in.
 * No check-sso: Keycloak CSP blocks the silent-check iframe.
 * Reload restore: pass persisted tokens into init() via initKeycloakOnce.
 */
export const keycloakInitOptions = {
  pkceMethod: 'S256' as const,
  checkLoginIframe: false,
  enableLogging: process.env.NODE_ENV === 'development',
  flow: 'standard' as const,
  responseMode: 'fragment' as const,
  scope: 'openid profile email groups',
};

export interface PersistedKeycloakTokens {
  token: string;
  refreshToken: string;
  idToken?: string;
}

/** keycloak-js allows init() only once per instance — cache the promise. */
let initPromise: Promise<boolean> | null = null;

export const initKeycloakOnce = (
  persisted?: PersistedKeycloakTokens
): Promise<boolean> => {
  if (!keycloakInstance) return Promise.resolve(false);
  if (!initPromise) {
    initPromise = keycloakInstance.init({
      ...keycloakInitOptions,
      ...(persisted && {
        token: persisted.token,
        refreshToken: persisted.refreshToken,
        idToken: persisted.idToken,
      }),
    }) as Promise<boolean>;
  }
  return initPromise as Promise<boolean>;
};
